import React, { useEffect, useState } from 'react';
import type { EventWritePayload, TaskRecord } from '../api';

export type EventEditorDraft = {
  title: string;
  description: string;
  eventDate: string;
  startTime: string;
  endTime: string;
  allDay: boolean;
  color: string;
  taskId: string | null;
};

interface Props {
  mode: 'create' | 'edit';
  draft: EventEditorDraft;
  tasks: TaskRecord[];
  onSave: (payload: EventWritePayload) => Promise<void>;
  onClose: () => void;
  onDelete?: () => Promise<void>;
}

const COLOR_OPTIONS = ['#3b82f6', '#22c55e', '#f59e0b', '#ef4444', '#8b5cf6'];

export function EventDetail({ mode, draft, tasks, onSave, onClose, onDelete }: Props) {
  const [form, setForm] = useState<EventEditorDraft>(draft);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    setForm(draft);
  }, [draft]);

  const save = async () => {
    if (!form.title.trim() || !form.eventDate) {
      return;
    }

    setSaving(true);
    try {
      await onSave({
        title: form.title.trim(),
        description: form.description.trim() || null,
        event_date: form.eventDate,
        start_time: form.allDay ? null : form.startTime || null,
        end_time: form.allDay ? null : form.endTime || null,
        all_day: form.allDay,
        color: form.color,
        task_id: form.taskId,
      });
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    if (!onDelete || !confirm('Delete this event?')) {
      return;
    }

    setDeleting(true);
    try {
      await onDelete();
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="detail-panel">
      <button className="close-btn" onClick={onClose}>✕</button>

      <div style={{ marginBottom: 20, marginTop: 8 }}>
        <h2 style={{ fontSize: 18, fontWeight: 700 }}>
          {mode === 'create' ? 'New event' : 'Edit event'}
        </h2>
        <p style={{ color: 'var(--text-muted)', fontSize: 12, marginTop: 4 }}>
          Keep calendar entries useful by capturing context, not just a title.
        </p>
      </div>

      <div className="form-group">
        <label htmlFor="event-title">Title</label>
        <input
          id="event-title"
          className="form-input"
          value={form.title}
          onChange={(event) => setForm((prev) => ({ ...prev, title: event.target.value }))}
          placeholder="Weekly planning, client call, bank visit..."
        />
      </div>

      <div className="form-group">
        <label htmlFor="event-date">Date</label>
        <input
          id="event-date"
          type="date"
          className="form-input"
          value={form.eventDate}
          onChange={(event) => setForm((prev) => ({ ...prev, eventDate: event.target.value }))}
          style={{ colorScheme: 'dark' }}
        />
      </div>

      <div className="form-group">
        <label>Event type</label>
        <div style={{ display: 'flex', gap: 6 }}>
          <button
            className={`chip ${form.allDay ? 'active' : ''}`}
            onClick={() => setForm((prev) => ({ ...prev, allDay: true, startTime: '', endTime: '' }))}
          >
            All day
          </button>
          <button
            className={`chip ${!form.allDay ? 'active' : ''}`}
            onClick={() => setForm((prev) => ({ ...prev, allDay: false }))}
          >
            Timed
          </button>
        </div>
      </div>

      {!form.allDay && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <div className="form-group">
            <label htmlFor="event-start">Start</label>
            <input
              id="event-start"
              type="time"
              className="form-input"
              value={form.startTime}
              onChange={(event) => setForm((prev) => ({ ...prev, startTime: event.target.value }))}
              style={{ colorScheme: 'dark' }}
            />
          </div>
          <div className="form-group">
            <label htmlFor="event-end">End</label>
            <input
              id="event-end"
              type="time"
              className="form-input"
              value={form.endTime}
              onChange={(event) => setForm((prev) => ({ ...prev, endTime: event.target.value }))}
              style={{ colorScheme: 'dark' }}
            />
          </div>
        </div>
      )}

      <div className="form-group">
        <label htmlFor="event-task-link">Linked task</label>
        <select
          id="event-task-link"
          className="form-input"
          value={form.taskId ?? ''}
          onChange={(event) => setForm((prev) => ({ ...prev, taskId: event.target.value || null }))}
        >
          <option value="">No linked task</option>
          {tasks.map((task) => (
            <option key={task.id} value={task.id}>{task.title}</option>
          ))}
        </select>
      </div>

      <div className="form-group">
        <label>Color</label>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {COLOR_OPTIONS.map((color) => (
            <button
              key={color}
              aria-label={`Use ${color} for this event`}
              className={`chip ${form.color === color ? 'active' : ''}`}
              onClick={() => setForm((prev) => ({ ...prev, color }))}
              style={{
                borderColor: form.color === color ? color : 'transparent',
                color: form.color === color ? color : 'var(--text-secondary)',
              }}
            >
              <span style={{ width: 10, height: 10, borderRadius: '50%', background: color, display: 'inline-block' }} />
              {color}
            </button>
          ))}
        </div>
      </div>

      <div className="form-group">
        <label htmlFor="event-description">Notes</label>
        <textarea
          id="event-description"
          className="form-input"
          rows={6}
          value={form.description}
          onChange={(event) => setForm((prev) => ({ ...prev, description: event.target.value }))}
          placeholder="Agenda, decisions, call notes, prep items..."
          style={{ resize: 'vertical', fontFamily: 'var(--font)' }}
        />
      </div>

      <div style={{ display: 'flex', gap: 8, marginTop: 20 }}>
        <button className="btn btn-primary" disabled={saving || !form.title.trim()} onClick={save}>
          {saving ? 'Saving...' : mode === 'create' ? 'Create event' : 'Save event'}
        </button>
        {onDelete && (
          <button
            className="btn btn-ghost"
            style={{ color: 'var(--danger)', borderColor: 'var(--danger)' }}
            onClick={remove}
            disabled={deleting}
          >
            {deleting ? 'Deleting...' : 'Delete'}
          </button>
        )}
      </div>
    </div>
  );
}
